import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Divider,
  useTheme,
  Skeleton,
  Chip,
  Tab,
  Tabs,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DualListMAIQ from 'app/components/DualListMAIQ';
import { MessageContentOperation } from 'app/apiService/messageContent/messageContentApiService';
import { ValueContent } from 'app/apiService/valueContent/valueContentApiService';
import { DealerSpecial } from 'app/apiService/dealerSpecials/dealerSpecialsApiService';
import { FooterImage } from 'app/apiService/footerImage/footerImageApiService';
import { LocalOperationState } from '../hooks/useMessageContent';

interface DualListItem {
  id: string;
  label: string;
  secondaryLabel?: string;
  trailingLabel?: string;
}

interface OperationAccordionProps {
  operation: MessageContentOperation;
  localState: LocalOperationState;
  allDealerValues: ValueContent[];
  allDealerSpecials: DealerSpecial[];
  allFooterImages: FooterImage[];
  isLoadingAvailable: boolean;
  messageContentId: string;
  onDealerValuesUpdate: (ids: string[]) => void;
  onDealerSpecialsUpdate: (ids: string[]) => void;
  onFooterImagesUpdate: (ids: string[]) => void;
  defaultExpanded?: boolean;
}

const DUAL_LIST_HEIGHT = 240;

const formatDate = (dateStr?: string): string | undefined => {
  if (!dateStr) return undefined;
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const toDealerValueItem = (dv: ValueContent): DualListItem => ({
  id: dv._id,
  label: dv.headline || dv._id || 'Unnamed Value',
  secondaryLabel: dv.value_type || undefined,
  trailingLabel: dv.end_date ? `Ends ${formatDate(dv.end_date)}` : undefined,
});

const toDealerSpecialItem = (ds: DealerSpecial): DualListItem => ({
  id: ds._id,
  label: ds.special_headline || ds._id || 'Unnamed Special',
  secondaryLabel: ds.special_description
    ? ds.special_description.length > 60
      ? ds.special_description.slice(0, 60) + '…'
      : ds.special_description
    : undefined,
  trailingLabel: ds.end_date ? `Ends ${formatDate(ds.end_date)}` : undefined,
});

const toFooterImageItem = (fi: FooterImage): DualListItem => ({
  id: fi._id,
  label: fi.image_description || fi._id || 'Unnamed Footer Image',
  secondaryLabel: fi.image_url || undefined,
  trailingLabel: fi.expiry_date ? `Expires ${formatDate(fi.expiry_date)}` : undefined,
});

/* ─── Hover Detail Card Builders ─── */

const DetailRow = ({ label, value }: { label: string; value?: string }) =>
  value ? (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  ) : null;

const buildDealerValueCard = (dv: ValueContent): React.ReactNode => (
  <Box>
    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3, mb: 0.75 }}>
        {dv.headline}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {dv.value_type && (
          <Chip label={dv.value_type} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
        )}
        <Chip
          label={dv.is_active ? 'Active' : 'Inactive'}
          size="small"
          color={dv.is_active ? 'success' : 'default'}
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      </Box>
    </Box>
    {dv.content && (
      <>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.4 }}>Content</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
            {dv.content.length > 140 ? dv.content.slice(0, 140) + '…' : dv.content}
          </Typography>
        </Box>
      </>
    )}
    {(dv.end_date || dv.label) && (
      <>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <DetailRow label="End Date" value={dv.end_date ? formatDate(dv.end_date) : undefined} />
          <DetailRow label="Label" value={dv.label} />
        </Box>
      </>
    )}
  </Box>
);

const buildDealerSpecialCard = (ds: DealerSpecial): React.ReactNode => (
  <Box>
    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3, mb: 0.75 }}>
        {ds.special_headline}
      </Typography>
      {ds.applicable_model && (
        <Chip label={ds.applicable_model} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
      )}
    </Box>
    {ds.special_description && (
      <>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.4 }}>Description</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
            {ds.special_description.length > 140 ? ds.special_description.slice(0, 140) + '…' : ds.special_description}
          </Typography>
        </Box>
      </>
    )}
    <Divider />
    <Box sx={{ px: 2, py: 1 }}>
      <DetailRow label="Start Date" value={ds.start_date ? formatDate(ds.start_date) : undefined} />
      <DetailRow label="End Date" value={ds.end_date ? formatDate(ds.end_date) : undefined} />
      {ds.disclaimer_text && <DetailRow label="Disclaimer" value={
        ds.disclaimer_text.length > 80 ? ds.disclaimer_text.slice(0, 80) + '…' : ds.disclaimer_text
      } />}
    </Box>
  </Box>
);

const buildFooterImageCard = (fi: FooterImage): React.ReactNode => (
  <Box>
    {/* Main preview — image_address is the actual CDN image URL */}
    {fi.image_address && (
      <Box
        component="img"
        src={fi.image_address}
        alt={fi.image_description || 'Footer Image'}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          e.currentTarget.style.display = 'none';
        }}
        sx={{ width: '100%', maxHeight: 140, objectFit: 'cover', display: 'block' }}
      />
    )}
    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
        {fi.image_description || 'Footer Image'}
      </Typography>
    </Box>
    <Divider />
    <Box sx={{ px: 2, py: 1 }}>
      {/* Image URL row — thumbnail + address text side by side */}
      {fi.image_address && (
        <Box sx={{ mb: 0.75 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.4 }}>
            Image URL
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box
              component="img"
              src={fi.image_address}
              alt=""
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
              }}
              sx={{ width: 40, height: 32, objectFit: 'cover', borderRadius: 1, flexShrink: 0, border: '1px solid', borderColor: 'divider' }}
            />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', wordBreak: 'break-all', color: 'text.secondary' }}>
              {fi.image_address.length > 55 ? fi.image_address.slice(0, 55) + '…' : fi.image_address}
            </Typography>
          </Box>
        </Box>
      )}
      <DetailRow label="Website URL" value={fi.image_url ? (fi.image_url.length > 50 ? fi.image_url.slice(0, 50) + '…' : fi.image_url) : undefined} />
      <DetailRow label="Expires" value={fi.expiry_date ? formatDate(fi.expiry_date) : undefined} />
    </Box>
  </Box>
);

const DualListSkeleton = () => (
  <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
    <Skeleton variant="rounded" width="100%" height={DUAL_LIST_HEIGHT + 80} />
    <Skeleton variant="rounded" width={60} height={DUAL_LIST_HEIGHT + 80} sx={{ flexShrink: 0 }} />
    <Skeleton variant="rounded" width="100%" height={DUAL_LIST_HEIGHT + 80} />
  </Box>
);

const TabLabel = ({
  icon,
  label,
  selected,
  total,
}: {
  icon: React.ReactNode;
  label: string;
  selected: number;
  total: number;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    {icon}
    <span>{label}</span>
    {total > 0 && (
      <Chip
        label={`${selected}/${total}`}
        size="small"
        color={selected > 0 ? 'primary' : 'default'}
        variant={selected > 0 ? 'filled' : 'outlined'}
        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: '5px' } }}
      />
    )}
  </Box>
);

const OperationAccordion = ({
  operation,
  localState,
  allDealerValues,
  allDealerSpecials,
  allFooterImages,
  isLoadingAvailable,
  messageContentId,
  onDealerValuesUpdate,
  onDealerSpecialsUpdate,
  onFooterImagesUpdate,
  defaultExpanded = false,
}: OperationAccordionProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const [activeTab, setActiveTab] = useState(0);

  const selectedDVIds = localState.dealer_values;
  const selectedDSIds = localState.dealer_specials;
  const selectedFIIds = localState.footer_image;

  const dvAvailable = allDealerValues
    .filter(dv => dv?._id && !selectedDVIds.includes(dv._id))
    .map(toDealerValueItem);
  const dvSelected = allDealerValues
    .filter(dv => dv?._id && selectedDVIds.includes(dv._id))
    .map(toDealerValueItem);

  const dsAvailable = allDealerSpecials
    .filter(ds => ds?._id && !selectedDSIds.includes(ds._id))
    .map(toDealerSpecialItem);
  const dsSelected = allDealerSpecials
    .filter(ds => ds?._id && selectedDSIds.includes(ds._id))
    .map(toDealerSpecialItem);

  const fiAvailable = allFooterImages
    .filter(fi => fi?._id && !selectedFIIds.includes(fi._id))
    .map(toFooterImageItem);
  const fiSelected = allFooterImages
    .filter(fi => fi?._id && selectedFIIds.includes(fi._id))
    .map(toFooterImageItem);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '10px !important',
        mb: 2,
        overflow: 'hidden',
        '&:before': { display: 'none' },
        transition: 'border-color 0.2s ease',
        '&.Mui-expanded': {
          borderColor: theme.palette.primary.main + '80',
          boxShadow: `0 0 0 1px ${theme.palette.primary.main}20`,
        },
      }}
    >
      {/* ─── Accordion Header ─── */}
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 3,
          minHeight: 64,
          backgroundColor: expanded
            ? theme.palette.primary.main + '08'
            : theme.palette.background.paper,
          '&:hover': { backgroundColor: theme.palette.action.hover },
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            my: 1.5,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor:
              selectedDVIds.length + selectedDSIds.length + selectedFIIds.length > 0
                ? theme.palette.primary.main
                : theme.palette.action.disabled,
            flexShrink: 0,
            mt: '2px',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ fontWeight: 700, lineHeight: 1.3, color: theme.palette.text.primary }}
          >
            {operation.display_name}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.68rem',
              color: theme.palette.text.disabled,
              letterSpacing: '0.3px',
            }}
          >
            {operation.type}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1, flexWrap: 'wrap' }}>
          {selectedDVIds.length > 0 && (
            <Chip
              icon={<StarOutlineIcon sx={{ fontSize: '13px !important' }} />}
              label={`${selectedDVIds.length} value${selectedDVIds.length > 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-label': { px: '6px' } }}
            />
          )}
          {selectedDSIds.length > 0 && (
            <Chip
              icon={<LocalOfferOutlinedIcon sx={{ fontSize: '13px !important' }} />}
              label={`${selectedDSIds.length} special${selectedDSIds.length > 1 ? 's' : ''}`}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-label': { px: '6px' } }}
            />
          )}
          {selectedFIIds.length > 0 && (
            <Chip
              icon={<ImageOutlinedIcon sx={{ fontSize: '13px !important' }} />}
              label={`${selectedFIIds.length} image${selectedFIIds.length > 1 ? 's' : ''}`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-label': { px: '6px' } }}
            />
          )}
        </Box>
      </AccordionSummary>

      {/* ─── Accordion Body ─── */}
      <AccordionDetails sx={{ p: 0 }}>
        <Divider />

        {/* Inner Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            px: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            minHeight: 44,
            '& .MuiTab-root': { minHeight: 44, fontSize: '0.8rem', textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab
            label={
              <TabLabel
                icon={<StarOutlineIcon sx={{ fontSize: 16 }} />}
                label="Dealer Values"
                selected={selectedDVIds.length}
                total={allDealerValues.length}
              />
            }
          />
          <Tab
            label={
              <TabLabel
                icon={<LocalOfferOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Dealer Specials"
                selected={selectedDSIds.length}
                total={allDealerSpecials.length}
              />
            }
          />
          <Tab
            label={
              <TabLabel
                icon={<ImageOutlinedIcon sx={{ fontSize: 16 }} />}
                label="Footer Images"
                selected={selectedFIIds.length}
                total={allFooterImages.length}
              />
            }
          />
        </Tabs>

        {/* Tab Panels */}
        {activeTab === 0 && (
          <Box sx={{ pt: 1, pb: 2 }}>
            {isLoadingAvailable ? (
              <DualListSkeleton />
            ) : (
              <DualListMAIQ
                key={`${operation.type}-dv-${messageContentId}`}
                availableItems={dvAvailable}
                selectedItems={dvSelected}
                listHeight={DUAL_LIST_HEIGHT}
                onUpdate={items => onDealerValuesUpdate(items.map(i => i.id))}
                renderHoverContent={id => {
                  const dv = allDealerValues.find(v => v._id === id);
                  return dv ? buildDealerValueCard(dv) : null;
                }}
              />
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ pt: 1, pb: 2 }}>
            {isLoadingAvailable ? (
              <DualListSkeleton />
            ) : (
              <DualListMAIQ
                key={`${operation.type}-ds-${messageContentId}`}
                availableItems={dsAvailable}
                selectedItems={dsSelected}
                listHeight={DUAL_LIST_HEIGHT}
                onUpdate={items => onDealerSpecialsUpdate(items.map(i => i.id))}
                renderHoverContent={id => {
                  const ds = allDealerSpecials.find(s => s._id === id);
                  return ds ? buildDealerSpecialCard(ds) : null;
                }}
              />
            )}
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ pt: 1, pb: 2 }}>
            {isLoadingAvailable ? (
              <DualListSkeleton />
            ) : (
              <DualListMAIQ
                key={`${operation.type}-fi-${messageContentId}`}
                availableItems={fiAvailable}
                selectedItems={fiSelected}
                listHeight={DUAL_LIST_HEIGHT}
                onUpdate={items => onFooterImagesUpdate(items.map(i => i.id))}
                renderHoverContent={id => {
                  const fi = allFooterImages.find(f => f._id === id);
                  return fi ? buildFooterImageCard(fi) : null;
                }}
              />
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default OperationAccordion;
